import React from "react";
import { Link } from "react-router-dom";
import { Fuel, Gauge, Cog, Tag, MessageCircle, Heart, Scale, Zap, Sparkles, Calendar } from "lucide-react";
import { imageUrl, formatPrice, formatKm } from "../lib/api";
import { usePublicSettings } from "../lib/publicSettings";
import { useWishlist } from "../lib/wishlist";
import { useCompare } from "../lib/compare";
import { toast } from "sonner";

const buildWhatsappUrl = (raw, car) => {
  if (!raw) return null;
  const digits = raw.replace(/[^0-9+]/g, "").replace(/^\+/, "");
  if (!digits) return null;
  const text = encodeURIComponent(
    `Ciao Rocco, sono interessato/a a questa auto: ${car.brand} ${car.model}` +
    (car.ref_code ? ` (Rif. ${car.ref_code})` : "") +
    `. Posso avere più informazioni?`
  );
  return `https://wa.me/${digits}?text=${text}`;
};

export const CarCard = ({ car, index = 0 }) => {
  const primary = car.images?.[0];
  const hasDiscount = car.is_on_sale && car.sale_price && car.sale_price < car.price;
  const displayPrice = hasDiscount ? car.sale_price : car.price;
  const saleBadge = car.is_on_sale ? (car.sale_label || "Promozione") : null;
  const settings = usePublicSettings();
  const waUrl = buildWhatsappUrl(settings?.whatsapp_number, car);
  const canContact = !car.is_sold && waUrl;
  const { has, toggle } = useWishlist();
  const saved = has(car.id);
  const compare = useCompare();
  const inCompare = compare.has(car.id);

  const onToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(car.id);
  };

  const onToggleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = compare.toggle(car.id);
    if (r && r.reason === "max") {
      toast.error(`Puoi confrontare massimo ${compare.max} auto`);
    }
  };

  return (
    <div
      className={`card-sharp overflow-hidden group block animate-fade-up ${car.is_on_sale ? "promo-card" : ""}`}
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms`, opacity: 0 }}
      data-testid={`car-card-${car.id}`}
    >
      <Link to={`/auto/${car.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-midnight">
          {primary ? (
            <img
              src={imageUrl(primary.storage_path)}
              alt={`${car.brand} ${car.model}`}
              className="w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">
              Nessuna foto
            </div>
          )}
          {car.is_sold && (
            <>
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden" data-testid={`sold-watermark-${car.id}`}>
                <div
                  className="text-white/45 font-display font-bold uppercase tracking-[0.35em] select-none whitespace-nowrap"
                  style={{
                    transform: "rotate(-22deg)",
                    fontSize: "clamp(2.8rem, 9vw, 5rem)",
                    textShadow: "0 4px 22px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.35)",
                    letterSpacing: "0.3em",
                  }}
                >
                  Venduta
                </div>
              </div>
              <div className="absolute inset-0 bg-midnight/25 pointer-events-none" aria-hidden="true" />
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-midnight text-white text-xs uppercase tracking-widest z-[2]">
                Venduta
              </div>
            </>
          )}
          {!car.is_sold && car.is_reserved && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-orange-500 text-white text-xs uppercase tracking-widest">
              Prenotata
            </div>
          )}
          {!car.is_sold && !car.is_reserved && saleBadge && (
            <div
              className="absolute top-4 left-4 px-3 py-1.5 bg-gold text-midnight text-xs uppercase tracking-widest font-bold flex items-center gap-1.5 shadow-md"
              data-testid={`sale-badge-${car.id}`}
            >
              <Tag size={11} strokeWidth={2.5} />
              {saleBadge}
            </div>
          )}
          {!car.is_sold && car.is_featured && (
            <div
              className={`absolute left-4 px-3 py-1.5 bg-midnight text-gold text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 shadow-md border border-gold/60 ${
                !car.is_reserved && saleBadge ? "top-14" : "top-4"
              }`}
              data-testid={`featured-badge-${car.id}`}
            >
              <Sparkles size={10} strokeWidth={2.5} />
              Vetrina
            </div>
          )}
          <button
            type="button"
            onClick={onToggleWishlist}
            aria-label={saved ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            aria-pressed={saved}
            data-testid={`wishlist-toggle-${car.id}`}
            className={`absolute top-4 right-4 h-9 w-9 flex items-center justify-center border transition-all ${
              saved
                ? "bg-gold text-midnight border-gold shadow-md scale-105"
                : "bg-white/95 backdrop-blur-md text-midnight/70 border-border hover:text-gold hover:border-gold"
            }`}
          >
            <Heart size={16} fill={saved ? "currentColor" : "none"} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={onToggleCompare}
            aria-label={inCompare ? "Rimuovi dal confronto" : "Aggiungi al confronto"}
            aria-pressed={inCompare}
            data-testid={`compare-toggle-${car.id}`}
            className={`absolute top-[3.75rem] right-4 h-9 w-9 flex items-center justify-center border transition-all ${
              inCompare
                ? "bg-midnight text-gold border-gold shadow-md scale-105"
                : "bg-white/95 backdrop-blur-md text-midnight/70 border-border hover:text-gold hover:border-gold"
            }`}
            title={inCompare ? "Rimuovi dal confronto" : "Aggiungi al confronto"}
          >
            <Scale size={15} strokeWidth={2.2} />
          </button>
        </div>

        <div className={`p-7 md:p-8 relative z-[2] ${car.is_on_sale ? "surface-silver" : ""}`}>
          {car.ref_code && (
            <div className="mb-3 flex items-center gap-2" data-testid={`car-ref-code-${car.id}`}>
              <span className="text-[11px] uppercase tracking-widest font-bold text-midnight">Rif.</span>
              <span
                className="font-mono text-sm font-bold tracking-wider text-midnight bg-white/85 px-2.5 py-1 border border-midnight/25 shadow-sm"
                aria-label={`Codice identificativo ${car.ref_code}`}
              >
                {car.ref_code}
              </span>
            </div>
          )}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="min-w-0">
              <p className="font-display text-2xl md:text-3xl uppercase tracking-wide text-midnight font-black mb-1.5 leading-none">
                {car.brand}
              </p>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-midnight leading-tight truncate">
                {car.model}
              </h3>
            </div>
            <div className="text-right shrink-0">
              {hasDiscount && (
                <p className="text-sm text-midnight/40 line-through leading-none mb-1" data-testid={`car-original-price-${car.id}`}>
                  {formatPrice(car.price)}
                </p>
              )}
              <p
                className={`font-display font-bold leading-none text-gold-outlined ${hasDiscount ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"}`}
                data-testid={`car-price-${car.id}`}
              >
                {formatPrice(displayPrice)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-x-3 gap-y-2 md:gap-x-5 text-sm text-midnight font-medium pt-4 border-t border-border flex-wrap">
            <span className="flex items-center gap-1.5" data-testid={`car-stat-year-${car.id}`}>
              <Calendar size={15} className="text-gold" />
              {car.year}
            </span>
            <span className="flex items-center gap-1.5" data-testid={`car-stat-km-${car.id}`}>
              <Gauge size={15} className="text-gold" />
              {formatKm(car.km)}
            </span>
            {car.fuel && (
              <span className="flex items-center gap-1.5" data-testid={`car-stat-fuel-${car.id}`}>
                <Fuel size={15} className="text-gold" />
                {car.fuel}
              </span>
            )}
            {car.transmission && (
              <span className="flex items-center gap-1.5" data-testid={`car-stat-transmission-${car.id}`}>
                <Cog size={15} className="text-gold" />
                {car.transmission}
              </span>
            )}
            {car.engine_cc && (
              <span className="flex items-center gap-1.5" data-testid={`car-stat-engine-${car.id}`}>
                <Zap size={15} className="text-gold" />
                {car.engine_cc} cc
              </span>
            )}
            {car.power_hp && (
              <span className="flex items-center gap-1.5" data-testid={`car-stat-hp-${car.id}`}>
                <Zap size={15} className="text-gold" />
                {car.power_hp} CV
              </span>
            )}
            {car.power_kw && (
              <span className="flex items-center gap-1.5" data-testid={`car-stat-kw-${car.id}`}>
                <Zap size={15} className="text-gold" />
                {car.power_kw} kW
              </span>
            )}
          </div>
        </div>
      </Link>

      {canContact && (
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center justify-center gap-2 w-full py-3 text-xs uppercase tracking-widest font-bold text-[#25D366] bg-transparent hover:bg-white/40 transition-colors border-t border-midnight/20"
          data-testid={`whatsapp-btn-${car.id}`}
          aria-label={`Contatta Rocco su WhatsApp per ${car.brand} ${car.model}`}
        >
          <svg width="16" height="16" viewBox="0 0 32 32" fill="#25D366" stroke="#000000" strokeWidth="1.2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 .5C7.44.5.5 7.44.5 16c0 2.83.75 5.6 2.16 8.05L.5 31.5l7.65-2.09A15.44 15.44 0 0 0 16 31.5C24.56 31.5 31.5 24.56 31.5 16S24.56.5 16 .5zm0 28.14a12.6 12.6 0 0 1-6.42-1.76l-.46-.27-4.54 1.24 1.22-4.42-.3-.46A12.63 12.63 0 1 1 16 28.64zm7.24-9.44c-.4-.2-2.35-1.16-2.71-1.3-.36-.13-.63-.2-.9.2-.26.4-1.03 1.3-1.26 1.56-.23.27-.47.3-.87.1-.4-.2-1.67-.62-3.18-1.96a11.9 11.9 0 0 1-2.2-2.74c-.23-.4-.02-.61.17-.81.18-.18.4-.47.6-.7.2-.24.27-.4.4-.67.14-.27.07-.5-.03-.7-.1-.2-.9-2.17-1.24-2.98-.32-.77-.66-.67-.9-.68l-.77-.02c-.27 0-.7.1-1.07.5-.37.4-1.4 1.37-1.4 3.34s1.43 3.87 1.63 4.14c.2.27 2.83 4.32 6.85 6.06.96.42 1.7.67 2.28.86.96.31 1.83.27 2.52.16.77-.12 2.35-.96 2.68-1.88.33-.93.33-1.72.23-1.88-.1-.16-.37-.27-.77-.47z"/>
          </svg>
          <span className="whatsapp-btn-label">Contatta su WhatsApp</span>
        </a>
      )}
    </div>
  );
};

export default CarCard;
